import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Link, useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { hasRole } from '@collector/shared'
import { api } from '../lib/api'
import { useAuth } from '../lib/auth'
import { displayPatientName, formatDateTime } from '../lib/format'
import { PageHeading } from '../components/PageHeading'

export function DataSetsPage() {
  const { t } = useTranslation()
  const auth = useAuth()
  const isAdmin = hasRole(auth.user, 'admin')
  const [params, setParams] = useSearchParams()
  const q = params.get('q') ?? ''
  const tabletName = params.get('tabletName') ?? ''
  const from = params.get('from') ?? ''
  const to = params.get('to') ?? ''
  const limit = Number.parseInt(params.get('limit') ?? '50', 10)
  const offset = Number.parseInt(params.get('offset') ?? '0', 10)

  const query = useMemo(
    () => ({
      ...(q && { q }),
      ...(tabletName && { tabletName }),
      ...(from && { from }),
      ...(to && { to }),
      limit,
      offset,
    }),
    [q, tabletName, from, to, limit, offset],
  )

  // Source the dropdown from tablet names actually present in stored data
  // sets — including ones from tablets that have since been revoked or
  // replaced — so the filter never silently loses values.
  const tabletNames = useQuery({
    queryKey: ['data-sets', 'tablet-names'],
    queryFn: () => api().dataSets.tabletNames(),
  })
  const dataSets = useQuery({
    queryKey: ['data-sets', query],
    queryFn: () => api().dataSets.list(query),
  })

  const updateParam = (key: string, value: string) => {
    const next = new URLSearchParams(params)
    if (value) next.set(key, value)
    else next.delete(key)
    next.delete('offset')
    setParams(next)
  }

  return (
    <div>
      <PageHeading title={t('dataSets.title')} description={t('dataSets.description')} />
      <div className="card mb-4 grid grid-cols-1 gap-3 sm:grid-cols-4">
        <FilterField label={t('dataSets.search')} value={q} onChange={(v) => updateParam('q', v)} />
        <div>
          <label className="label">{t('dataSets.tablet')}</label>
          <select
            className="input"
            value={tabletName}
            onChange={(e) => {
              updateParam('tabletName', e.target.value)
            }}
          >
            <option value="">{t('common.any')}</option>
            {(tabletNames.data ?? []).map((name) => (
              <option key={name} value={name}>
                {name}
              </option>
            ))}
          </select>
        </div>
        <FilterField
          label={t('dataSets.from')}
          value={from}
          type="datetime-local"
          onChange={(v) => updateParam('from', v)}
        />
        <FilterField
          label={t('dataSets.to')}
          value={to}
          type="datetime-local"
          onChange={(v) => updateParam('to', v)}
        />
      </div>

      {dataSets.isLoading ? (
        <p className="text-sm text-stone-500 dark:text-stone-400">{t('common.loading')}</p>
      ) : dataSets.data && dataSets.data.items.length > 0 ? (
        <div className="overflow-x-auto rounded-md border border-stone-200 bg-white dark:border-stone-700 dark:bg-stone-900">
          <table className="min-w-full divide-y divide-stone-200 text-sm dark:divide-stone-700">
            <thead>
              <tr className="table-header">
                <th className="px-3 py-2">{t('dataSets.patient')}</th>
                <th className="px-3 py-2">{t('dataSets.tablet')}</th>
                <th className="px-3 py-2">{t('dataSets.captured')}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-stone-100 dark:divide-stone-800">
              {dataSets.data.items.map((d) => (
                <tr key={d.id}>
                  <td className="px-3 py-2">
                    <Link
                      className="font-medium text-stone-900 hover:underline dark:text-stone-100"
                      to={`/data-sets/${d.id}`}
                    >
                      {displayPatientName(d.patient, isAdmin)}
                    </Link>
                  </td>
                  <td className="px-3 py-2">{d.tabletName}</td>
                  <td className="px-3 py-2 text-stone-600 dark:text-stone-400">
                    {formatDateTime(d.capturedAt)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="flex items-center justify-between border-t border-stone-200 px-3 py-2 text-xs text-stone-600 dark:border-stone-700 dark:text-stone-400">
            <span>{t('dataSets.resultCount', { count: dataSets.data.total })}</span>
            <div className="flex items-center gap-2">
              <button
                className="btn-secondary"
                disabled={offset === 0}
                onClick={() => {
                  const next = new URLSearchParams(params)
                  next.set('offset', String(Math.max(0, offset - limit)))
                  setParams(next)
                }}
              >
                {t('common.previous')}
              </button>
              <button
                className="btn-secondary"
                disabled={offset + limit >= dataSets.data.total}
                onClick={() => {
                  const next = new URLSearchParams(params)
                  next.set('offset', String(offset + limit))
                  setParams(next)
                }}
              >
                {t('common.next')}
              </button>
            </div>
          </div>
        </div>
      ) : (
        <p className="text-sm text-stone-500 dark:text-stone-400">{t('dataSets.noneMatch')}</p>
      )}
    </div>
  )
}

function FilterField({
  label,
  value,
  onChange,
  type = 'text',
}: {
  label: string
  value: string
  onChange: (v: string) => void
  type?: string
}) {
  return (
    <div>
      <label className="label">{label}</label>
      <input
        className="input"
        type={type}
        value={value}
        onChange={(e) => {
          onChange(e.target.value)
        }}
      />
    </div>
  )
}
